import { t, Trans } from '@lingui/macro'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import { Avatar, Box, Button, Menu, MenuItem, Tooltip } from '@mui/material'
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar'
import MuiDrawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import { CSSObject, styled, Theme } from '@mui/material/styles'
import Toolbar from '@mui/material/Toolbar'
import { useTheme } from '@mui/system'
import { useAuth, useUserPermissions } from '@om1/platform-authentication'
import { LightTooltip } from '@om1/platform-components/Tooltip'
import { Routes, toPath } from '@om1/platform-routing'
import { PlatformPermissions } from '@om1/platform-utils'
import * as React from 'react'
import { ComponentType, createContext, ReactElement, useCallback, useContext, useEffect, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { isCohortBuilderPrintViewUrl } from '../../../../../apps/platform/src/pages/CohortPage'
import { MenuItemLabel } from '../appbar/MainMenuItem'
import PermissionProtectedSideBarItem from '../PermissionProtectedSideBarItem'
import { VersionModal } from '../VersionModal'
import { OrganizedSideBar } from './OrganizedSideBar'

// Create context for sidebar state
interface SidebarContextType {
    isOpen: boolean
    drawerWidth: number
}

const SidebarContext = createContext<SidebarContextType>({
    isOpen: false,
    drawerWidth: 200
})

export const useSidebar = () => useContext(SidebarContext)

const StyledArrowDropDownIcon = styled(ArrowDropDownIcon)`
    color: white;
`

const StyledQuestionMarkIcon = styled(QuestionMarkIcon)`
    background: white;
    color: #002d72;
    border-radius: 12px;
    height: 15px;
    width: 15px;
    padding: 2px;
`

const drawerWidth = 200

const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen
    }),
    overflowX: 'hidden'
})

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
    }),
    overflowX: 'hidden',
    width: '65px',
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`
    }
})

interface AppBarProps extends MuiAppBarProps {
    open?: boolean
}

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open'
})<AppBarProps>(({ theme, open }) => ({
    position: 'fixed',
    top: 0,
    zIndex: theme.zIndex.drawer + 1,
    marginLeft: open ? drawerWidth : '65px',
    width: open ? `calc(100% - ${drawerWidth}px)` : `calc(100% - 65px)`,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
    })
}))

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    height: '100vh',
    boxSizing: 'border-box',
    position: 'fixed',
    top: 0,
    left: 0,
    zIndex: theme.zIndex.drawer,
    ...(open && {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': {
            ...openedMixin(theme),
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100vh'
        }
    }),
    ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': {
            ...closedMixin(theme),
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100vh'
        }
    })
}))

export type SideBarItem = {
    route: string
    name: React.ReactElement<any, any>
    icon: React.ReactElement<any, any>
    permissions?: PlatformPermissions[]
    userPermissions?: string[]
}

const SideBarListItem: React.FunctionComponent<
    SideBarItem & { active: boolean; open: boolean; onClick: React.MouseEventHandler<HTMLAnchorElement> }
> = (props) => {
    // Check if this is the home route to apply larger font size
    const isHomeRoute = props.route === 'home'

    // Clone icon with conditional color based on active state
    // Handle both Material-UI icons (with sx prop) and Lucide React icons (with color prop)
    const targetColor = props.active ? '#002D72' : '#333333'
    const iconWithColor = React.cloneElement(props.icon, {
        ...(props.icon.props.sx
            ? {
                  // Material-UI icon with sx prop
                  sx: {
                      ...props.icon.props.sx,
                      color: targetColor
                  }
              }
            : {
                  // Lucide React icon with color prop
                  color: targetColor
              })
    })

    const item = (
        <ListItem key={props.name.key} disablePadding sx={{ display: 'block', backgroundColor: props.active ? '#E3F2FD' : '#f5f6f6' }}>
            <Button
                component={Link}
                to={toPath(props.route as Routes)}
                sx={{
                    height: 40,
                    justifyContent: 'initial',
                    alignItems: 'center',
                    px: 2.4,
                    width: '100%'
                }}
                onClick={props.onClick}
            >
                <LightTooltip title={props.name} placement='right' enterDelay={500} sx={{ opacity: props.open ? 0 : 1 }}>
                    <ListItemIcon
                        sx={{
                            minWidth: 0,
                            mr: 3,
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: 24,
                            height: 24
                        }}
                    >
                        {iconWithColor}
                    </ListItemIcon>
                </LightTooltip>
                <ListItemText
                    primary={props.name}
                    sx={{ opacity: props.open ? 1 : 0 }}
                    primaryTypographyProps={{
                        sx: {
                            fontSize: '14px',
                            fontWeight: 'bold',
                            letterSpacing: '0',
                            lineHeight: isHomeRoute ? '16px' : '14px',
                            color: props.active ? '#002D72' : '#333333'
                        }
                    }}
                />
            </Button>
        </ListItem>
    )
    if (props.permissions && props.permissions.length > 0) {
        return (
            <PermissionProtectedSideBarItem permissions={props.permissions} userPermissions={props.userPermissions}>
                {item}
            </PermissionProtectedSideBarItem>
        )
    } else {
        return item
    }
}

export { SideBarListItem }

export const ResponsiveSideBar: React.FunctionComponent<
    React.PropsWithChildren<{
        logo?: React.ReactElement
        sideBarItems: {
            route: string
            component: ComponentType<any>
            name: ReactElement<any, any>
            icon: ReactElement<any, any>
            permissions?: PlatformPermissions[]
        }[]
        appSemanticVersion: string
        jenkinsBuildNumber: string
        environmentTag: string
        falconApiVersion: string
        falconAlembicVersion: string
        showHelpDialog?: () => void
        sidebarSections?: any[]
    }>
> = (props) => {
    const { showHelpDialog } = props
    const theme = useTheme()
    const [open, setOpen] = React.useState(false)
    const [versionModalOpen, setVersionModalOpen] = React.useState(false)
    const { user, logout } = useAuth()

    // Removed debug logging to prevent memory leaks

    const { permissions, decodedToken } = useUserPermissions()
    const orgName = decodedToken?.org_name

    const toggleDrawerOpen = useCallback(() => {
        setOpen(!open)
    }, [open])

    const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null)

    const handleOpenUserMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setAnchorElUser(event.currentTarget)
    }, [])

    const handleCloseUserMenu = useCallback(() => {
        setAnchorElUser(null)
    }, [])

    const handleOpenVersionModal = useCallback(() => {
        setVersionModalOpen(true)
    }, [])

    const handleCloseVersionModal = useCallback(() => {
        setVersionModalOpen(false)
    }, [])

    const handleHelpDialogOpen = useCallback(() => {
        if (showHelpDialog) {
            showHelpDialog()
        }
    }, [showHelpDialog])

    const location = useLocation()

    let frontendVersion = 'develop'
    if (props.jenkinsBuildNumber !== 'develop') {
        frontendVersion = props.appSemanticVersion.replace(/(.*\.)(.*\.)(.*)/, `$1$2${props.jenkinsBuildNumber}`)
    }

    const headerless: boolean = useMemo(() => {
        return isCohortBuilderPrintViewUrl(location.pathname)
    }, [location.pathname])

    const avatarStyle = useMemo(
        () => ({
            borderRadius: '20px',
            color: '#002D72',
            backgroundColor: 'white',
            margin: '10px',
            width: '30px',
            height: '30px'
        }),
        []
    )

    const userMenuButtonStyle = useMemo(
        () => ({
            p: 0,
            borderRadius: '0px',
            paddingLeft: '30px',
            marginRight: '10px',
            height: '30px'
        }),
        []
    )

    const menuStyle = useMemo(
        () => ({
            mt: '18px',
            ml: '-10px'
        }),
        []
    )

    useEffect(() => {
        /* eslint-disable-next-line */
        window.appVersion = `frontend: ${frontendVersion}, backend: ${props.falconApiVersion}, db: ${props.falconAlembicVersion}, env: ${props.environmentTag}`
        // Removed console.log to prevent memory leaks
    }, [frontendVersion, props.falconApiVersion, props.falconAlembicVersion, props.environmentTag])

    // Set CSS custom properties for sidebar state
    useEffect(() => {
        document.documentElement.style.setProperty('--sidebar-width', open ? `${drawerWidth}px` : '65px')
        document.documentElement.style.setProperty('--sidebar-open', open ? '1' : '0')
    }, [open, drawerWidth])
    if (location) {
        return (
            <Box sx={{ display: 'flex', flex: 1, minWidth: 0 }}>
                <AppBar position='fixed' open={open}>
                    <Toolbar variant='dense'>
                        {props.logo}
                        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }} />
                        {user && orgName && orgName !== 'om1' && (
                            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>{orgName.toUpperCase()}</Box>
                        )}
                        <Box sx={{ display: 'flex' }}>
                            <IconButton onClick={handleOpenUserMenu} sx={userMenuButtonStyle}>
                                <Avatar sx={avatarStyle}>
                                    {user?.nickname ? user?.nickname[0] : ''}
                                    {user?.nickname ? user?.nickname[1] : ''}
                                </Avatar>
                                <MenuItemLabel>
                                    {user?.given_name ? <>{user?.given_name}</> : user?.nickname ? <>{user?.nickname}</> : <Trans>Account</Trans>}
                                </MenuItemLabel>
                                <StyledArrowDropDownIcon />
                            </IconButton>
                            <Menu
                                sx={menuStyle}
                                id='menu-appbar-large'
                                anchorEl={anchorElUser}
                                anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right'
                                }}
                                keepMounted
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right'
                                }}
                                open={Boolean(anchorElUser)}
                                onClose={handleCloseUserMenu}
                            >
                                <MenuItem key={t`Version`} onClick={handleOpenVersionModal}>
                                    <Trans>Version</Trans>
                                </MenuItem>
                                <MenuItem key={t`Logout`} onClick={() => logout()}>
                                    <Trans>Logout</Trans>
                                </MenuItem>
                            </Menu>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Tooltip title={<Trans>Need Help?</Trans>} sx={{ p: 0 }} id={'helpTooltip'}>
                                <IconButton sx={{ p: 0 }} key={'questionMarkIcon'} aria-label='help' onClick={handleHelpDialogOpen}>
                                    <StyledQuestionMarkIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Toolbar>
                </AppBar>
                {!headerless && (
                    <Drawer variant='permanent' open={open}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: open ? 'flex-end' : 'center',
                                padding: '8px',
                                backgroundColor: '#f5f6f6'
                            }}
                        >
                            <IconButton
                                onClick={toggleDrawerOpen}
                                sx={{
                                    backgroundColor: '#f5f6f6',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '4px',
                                    width: '24px',
                                    height: '24px',
                                    '&:hover': {
                                        backgroundColor: '#e0e0e0'
                                    }
                                }}
                            >
                                {theme.direction === 'rtl' ? (
                                    open ? (
                                        <ChevronRightIcon sx={{ fontSize: '16px' }} />
                                    ) : (
                                        <ChevronLeftIcon sx={{ fontSize: '16px' }} />
                                    )
                                ) : open ? (
                                    <ChevronLeftIcon sx={{ fontSize: '16px' }} />
                                ) : (
                                    <ChevronRightIcon sx={{ fontSize: '16px' }} />
                                )}
                            </IconButton>
                        </Box>
                        <OrganizedSideBar
                            items={props.sideBarItems.map((item) => ({
                                route: item.route,
                                name: item.name,
                                icon: item.icon,
                                permissions: item.permissions || [],
                                userPermissions: permissions
                            }))}
                            open={open}
                            onItemClick={() => {}}
                            activeRoute={location.pathname}
                            sections={props.sidebarSections}
                        />
                    </Drawer>
                )}
                <Box
                    paddingTop={7}
                    component='main'
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        minWidth: 0,
                        marginLeft: open ? `${drawerWidth}px` : '65px',
                        transition: 'margin-left 0.3s ease'
                    }}
                >
                    <SidebarContext.Provider value={{ isOpen: open, drawerWidth }}>{user && props.children}</SidebarContext.Provider>
                </Box>

                <VersionModal
                    open={versionModalOpen}
                    onClose={handleCloseVersionModal}
                    appSemanticVersion={props.appSemanticVersion}
                    jenkinsBuildNumber={props.jenkinsBuildNumber}
                    environmentTag={props.environmentTag}
                />
            </Box>
        )
    }
    return null
}
